import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { AlertTriangle, CheckCircle2, Loader2 } from "lucide-react";
import { CoverageIcon } from "@/components/benefits/v2/typeIcons";

type OfferedPlan = {
  id: number;
  name?: string | null;
  carrier?: string | null;
  type?: string | null;
  coverageType?: string | null;
};

/**
 * Record an acceptance agreed away from the platform — a phone call, an email,
 * a meeting.
 *
 * Without this a quote the client said yes to over the phone stayed at "sent"
 * forever, because every acceptance had to start with the client submitting
 * through the public link, and the broker could never set up their plans.
 *
 * The broker picks which plans were agreed: a quote often offers options the
 * client was meant to choose between, so taking all of them would create plans
 * nobody picked.
 */
export function MarkAcceptedDialog({
  quote,
  onOpenChange,
}: {
  quote: any | null;
  onOpenChange: (open: boolean) => void;
}) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const offered: OfferedPlan[] = Object.values(
    ((quote?.wizardState?.selectedPlans ?? {}) as Record<string, OfferedPlan[]>) ?? {},
  ).flat();

  const [picked, setPicked] = useState<number[]>([]);
  const [note, setNote] = useState("");

  useEffect(() => {
    // Nothing is pre-ticked: which plans were agreed is exactly the thing the
    // platform does not know, and a wrong default here creates real plans for
    // the client.
    setPicked([]);
    setNote("");
  }, [quote?.id]);

  const toggle = (planId: number) =>
    setPicked((prev) =>
      prev.includes(planId) ? prev.filter((id) => id !== planId) : [...prev, planId],
    );

  const mutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/quotes/${quote.id}/record-acceptance`, {
        planIds: picked,
        note: note.trim() || undefined,
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.message || "Couldn't record the acceptance");
      return body;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/quotes"] });
      toast({
        title: "Marked as accepted",
        description: "Set up the client's plans from the quote.",
      });
      onOpenChange(false);
    },
    onError: (error: Error) =>
      toast({
        title: "Couldn't mark it accepted",
        description: error.message,
        variant: "destructive",
      }),
  });

  return (
    <Dialog open={!!quote} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Mark this quote as accepted</DialogTitle>
          <DialogDescription>
            For an agreement reached outside the platform. Tick the plans the client actually
            agreed to — only those become their benefits.
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
          <p className="flex items-start gap-1.5">
            <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            <span>
              No client signed this in the platform, so there is no signature and no record of the
              contribution split they agreed. You'll enter the contributions yourself when setting
              up the plans.
            </span>
          </p>
        </div>

        {offered.length === 0 ? (
          <p className="py-6 text-sm text-muted-foreground">
            This quote has no plans on it, so there is nothing to accept. Reopen it and add plans
            first.
          </p>
        ) : (
          <div className="space-y-2">
            {offered.map((plan) => {
              const isPicked = picked.includes(plan.id);
              return (
                <button
                  key={plan.id}
                  type="button"
                  onClick={() => toggle(plan.id)}
                  aria-pressed={isPicked}
                  className={`w-full flex items-center gap-3 p-3 rounded-lg border text-left transition-all ${
                    isPicked ? "ring-2 ring-primary bg-primary/5" : "hover:bg-muted/40"
                  }`}
                >
                  <Checkbox checked={isPicked} className="pointer-events-none" />
                  <CoverageIcon planType={plan.type ?? plan.coverageType} size="sm" />
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm font-medium truncate">{plan.name}</span>
                    <span className="block text-[11px] text-muted-foreground truncate">
                      {plan.carrier}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        )}

        <div>
          <Label className="text-xs">How it was agreed (optional)</Label>
          <Textarea
            className="mt-1 min-h-[60px] text-sm"
            placeholder="Confirmed by phone with Jane on 14 Sept…"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </div>

        <DialogFooter className="sm:justify-between gap-3 border-t pt-4">
          <p className="text-xs text-muted-foreground">
            {picked.length === 0
              ? "Tick the plans that were agreed."
              : `${picked.length} plan${picked.length === 1 ? "" : "s"} will become their benefits.`}
          </p>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              disabled={picked.length === 0 || mutation.isPending}
              onClick={() => mutation.mutate()}
            >
              {mutation.isPending ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <CheckCircle2 className="h-4 w-4 mr-2" />
              )}
              Mark as accepted
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
